import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AudioEditor } from '@radioking/library/waveform/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, pluck, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import WaveformData from 'waveform-data';

function formatNumber(number: number) {
  if (number.toString().length >= 2) {
    return number;
  }

  return `0${number}`;
}

@Component({
  selector: 'rk-waveform-ui',
  templateUrl: './waveform-ui.component.html',
  styleUrls: ['./waveform-ui.component.scss'],
})
export class WaveformUiComponent implements AfterViewInit, OnDestroy {
  @ViewChild('overviewContainer', { static: true })
  overviwContainer: ElementRef<HTMLDivElement>;
  @ViewChild('zoomedContainer', { static: true })
  zoomedContainer: ElementRef<HTMLDivElement>;

  @Input() audioUrl: string;
  @Input() trackDuration: number;

  @Input() audioStart: number;
  @Input() audioEnd: number;

  @Input() isMixActive: boolean;
  @Input() mixValue: number;

  @Input() isFadeInActive: boolean;
  @Input() fadeInValue: number;

  @Input() isFadeOutActive: boolean;
  @Input() fadeOutValue: number;

  @Input() waveformUrl: string;

  @Output() readonly mixChanged = new EventEmitter<number>();
  @Output() readonly fadeInChanged = new EventEmitter<number>();
  @Output() readonly fadeOutChanged = new EventEmitter<number>();
  @Output() readonly startChanged = new EventEmitter<number>();
  @Output() readonly endChanged = new EventEmitter<number>();
  @Output() readonly isPlaying = new EventEmitter<boolean>();
  @Output() readonly durationChanged = new EventEmitter<number>();

  private audioEditor: AudioEditor;
  private readonly unsubscribeAll = new Subject();
  private previousSoundObject: object;
  private nextSoundObject: object;
  canZoom = true;

  duration$: Observable<[string, number]>;
  cursor$: Observable<[string, number]>;

  isLoading = true;

  zoom = 1;

  @Input()
  set previousTrack(track: object) {
    this.previousSoundObject = track;
    if (this.audioEditor && this.audioEditor.player) {
      this.audioEditor.setPreviousSound(this.previousSoundObject);
    }
  }

  @Input()
  set nextTrack(track: object) {
    this.nextSoundObject = track;
    if (this.audioEditor && this.audioEditor.player) {
      this.audioEditor.setNextSound(this.nextSoundObject);
    }
  }

  constructor(private readonly ngZone: NgZone) {}

  ngAfterViewInit() {
    setTimeout(() => {
      this.createAudioEditor();
    });
  }

  createAudioEditor() {
    this.audioEditor = new AudioEditor({
      audioUrl: this.audioUrl,
      waveformUrl: this.waveformUrl,
      waveformColor: '#FFC4AE',
      backgroundColor: '#FFF2ED',
      cursorColor: '#000000',
      segment: {
        from: this.audioStart,
        to: this.audioEnd,
      },
      intro: {
        at: this.fadeInValue,
        color: '#FF4757',
        isActive: this.isFadeInActive,
      },
      outro: {
        at: this.fadeOutValue,
        color: '#1E90FF',
        isActive: this.isFadeOutActive,
      },
      mix: {
        at: this.mixValue,
        color: '#FFA502',
        isActive: this.isMixActive,
      },
      overview: {
        mountPoint: this.overviwContainer.nativeElement,
        height: 50,
        padding: 20,
      },
      zoom: {
        mountPoint: this.zoomedContainer.nativeElement,
        height: 200,
        padding: 10,
      },
      selectionBox: {
        backgroundColor: '#FFF2ED',
        waveformColor: '#FF7F50',
      },
      viewBox: {
        backgroundColor: '#474747',
        handlesColor: '#474747',
        colorBound: '#2ED573',
      },
      axis: {
        backgroundColor: '#F9F9F9',
        fontColor: '#9c9c9c',
      },
    });
    if (this.previousSoundObject && this.audioEditor.player) {
      this.audioEditor.setPreviousSound(this.previousSoundObject);
    }
    if (this.nextSoundObject && this.audioEditor.player) {
      this.audioEditor.setNextSound(this.nextSoundObject);
    }
    this.ngZone.run(() => {
      this.setupListeners();
    });
  }

  setupListeners() {
    this.audioEditor.player.isPlaying$
      .pipe(takeUntil(this.unsubscribeAll), startWith(false))
      .subscribe(val => this.isPlaying.emit(val));

    this.audioEditor.introPointChanged$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(val => this.fadeInChanged.emit(val));

    this.audioEditor.mixPointChanged$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(val => this.mixChanged.emit(val));

    this.audioEditor.outroPointChanged$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(val => this.fadeOutChanged.emit(val));

    const onStartChanged$ = this.audioEditor.onStartChanged$.pipe(
      takeUntil(this.unsubscribeAll),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    onStartChanged$.subscribe(val => this.startChanged.emit(val));

    const onEndChanged$ = this.audioEditor.onEndChanged$.pipe(
      takeUntil(this.unsubscribeAll),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    onEndChanged$.subscribe(val => {
      // Floor to avoid precision issue
      this.endChanged.emit(Math.floor(val * 1000) / 1000);
    });

    this.audioEditor.onWaveformReadyToDraw$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((waveform: WaveformData) => {
        this.durationChanged.emit(Math.floor(waveform.duration * 1000) / 1000);
        this.zoomIn();
        this.audioEditor.player.setTime(0);
        this.isLoading = false;
        this.audioEditor.overviewWaveform.moveToStart();
      });

    this.audioEditor.onWaveformZoom$
      .pipe(takeUntil(this.unsubscribeAll), pluck('zoom'))
      .subscribe(val => (this.zoom = val));

    this.duration$ = combineLatest([
      onStartChanged$.pipe(startWith(this.audioStart)),
      onEndChanged$.pipe(startWith(this.audioEnd)),
    ]).pipe(
      map(([start, end]) => end),
      map(this.fromTimeToArray),
    );

    this.cursor$ = this.audioEditor.onPlayerChanged$.pipe(
      pluck('currentTime'),
      map(this.fromTimeToArray),
    );
  }

  ngOnDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
    if (this.audioEditor) {
      this.audioEditor.onDestroy();
    }
  }

  fromTimeToArray(time: number): [string, number] {
    const h = Math.floor(time / 3600);
    const mns = Math.floor(time / 60) - 60 * h;
    const secs = Math.floor(time) - 60 * mns - 3600 * h;
    const ms = Math.floor((time % 1) * 1000);
    const left = [h, mns, secs].map(formatNumber).join(':');

    return [left, ms];
  }

  zoomIn() {
    if (this.audioEditor) {
      this.checkZoomInAvailable();
      this.audioEditor.zoomWaveform.zoomIn();
    }
  }

  zoomOut() {
    if (this.audioEditor) {
      this.canZoom = true;
      this.audioEditor.zoomWaveform.zoomOut();
    }
  }

  checkZoomInAvailable() {
    const { duration, sample_rate, scale } = this.audioEditor.waveformData;
    const newScale = Math.floor((duration * sample_rate) / (this.zoom * 1100 * 4));
    this.canZoom = newScale >= scale;
  }

  setMixAtCursor() {
    if (this.audioEditor) {
      this.audioEditor.setMixAtCursor();
    }
  }

  setMix(time: number) {
    if (time === this.audioEditor.segmentBar.mixPoint) {
      return;
    }
    if (this.audioEditor) {
      this.audioEditor.segmentBar.setMixPoint(time);
    }
    if (time !== this.audioEditor.segmentBar.mixPoint) {
      this.mixChanged.emit(this.audioEditor.segmentBar.mixPoint);
    }
  }

  removeMix() {
    if (this.audioEditor) {
      this.audioEditor.segmentBar.hideMixPoint();
    }
  }

  setFadeInAtCursor() {
    if (this.audioEditor) {
      this.audioEditor.setIntroAtCursor();
    }
  }

  setFadeIn(time: number) {
    if (time === this.audioEditor.segmentBar.introPoint) {
      return;
    }
    if (this.audioEditor) {
      this.audioEditor.segmentBar.setIntroPoint(time);
    }
    if (time !== this.audioEditor.segmentBar.introPoint) {
      this.fadeInChanged.emit(this.audioEditor.segmentBar.introPoint);
    }
  }

  removeFadeIn() {
    if (this.audioEditor) {
      this.audioEditor.segmentBar.hideIntroPoint();
    }
  }

  setFadeOutAtCursor() {
    if (this.audioEditor) {
      this.audioEditor.setOutroAtCursor();
    }
  }

  setFadeOut(time: number) {
    if (time === this.audioEditor.segmentBar.outroPoint) {
      return;
    }
    if (this.audioEditor) {
      this.audioEditor.segmentBar.setOutroPoint(time);
    }
    if (time !== this.audioEditor.segmentBar.outroPoint) {
      this.fadeOutChanged.emit(this.audioEditor.segmentBar.outroPoint);
    }
  }

  removeFadeOut() {
    if (this.audioEditor) {
      this.audioEditor.segmentBar.hideOutroPoint();
    }
  }

  playIntro() {
    if (this.audioEditor) {
      this.audioEditor.player.playIntro();
    }
  }

  playEnd() {
    if (this.audioEditor) {
      this.audioEditor.player.playEnd();
    }
  }

  playOrPause() {
    if (this.audioEditor) {
      this.audioEditor.player.playOrPause();
    }
  }

  setStartAtCursor() {
    if (this.audioEditor) {
      this.audioEditor.setStartAtCursor();
    }
  }

  setEndAtCursor() {
    if (this.audioEditor) {
      this.audioEditor.setEndAtCursor();
    }
  }

  resetStartOfTrack() {
    if (this.audioEditor) {
      this.audioEditor.selectionBox.resetStart();
    }
  }

  resetEndOfTrack() {
    if (this.audioEditor) {
      this.audioEditor.selectionBox.resetEnd();
    }
  }

  setEnd(time: number) {
    if (time === this.audioEditor.selectionBox.to) {
      return;
    }
    if (this.audioEditor) {
      this.audioEditor.selectionBox.setEnd(time);
    }
    if (time !== this.audioEditor.selectionBox.to) {
      this.endChanged.emit(this.audioEditor.selectionBox.to);
    }
  }

  setStart(time: number) {
    if (time === this.audioEditor.selectionBox.from) {
      return;
    }
    if (this.audioEditor) {
      this.audioEditor.selectionBox.setStart(time);
    }
    if (time !== this.audioEditor.selectionBox.from) {
      this.startChanged.emit(this.audioEditor.selectionBox.from);
    }
  }
}
